// src/pages/GalleryPage.tsx - BENTO GRID
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../config/supabaseClient';
import { Post } from '../services/postService';
import toast from 'react-hot-toast';
import {
  FaPlay,
  FaHeart,
  FaComment,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaBookmark,
  FaShare,
  FaStar,
  FaImages,
  FaVideo,
  FaFilter,
  FaExpand
} from 'react-icons/fa';

interface GalleryPageProps {
  darkMode: boolean;
  userId?: string;
}

// Bento pattern cycles every 7 items:
// [0] large (col-span-2, row-span-2)
// [1] tall (col-span-1, row-span-2)
// [2] normal
// [3] normal
// [4] wide (col-span-2, row-span-1)
// [5] normal
// [6] normal
const getBentoClass = (index: number): string => {
  const pos = index % 7;
  switch (pos) {
    case 0: return 'col-span-2 row-span-2';
    case 1: return 'col-span-1 row-span-2';
    case 4: return 'col-span-2 row-span-1';
    default: return 'col-span-1 row-span-1';
  }
};

export default function GalleryPage({ darkMode, userId }: GalleryPageProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [filter, setFilter] = useState<'all' | 'images' | 'videos'>('all');
  const [likedPosts, setLikedPosts] = useState(new Set<string>());
  const [savedPosts, setSavedPosts] = useState(new Set<string>());

  const filterRef = useRef(filter);
  const loadingRef = useRef(loading);
  const hasMoreRef = useRef(hasMore);
  const postsRef = useRef(posts);
  const isFetchingRef = useRef(false);

  useEffect(() => { filterRef.current = filter; }, [filter]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { postsRef.current = posts; }, [posts]);

  const observerTarget = useRef<HTMLDivElement | null>(null);

  const fetchGalleryPosts = useCallback(async (limit: number, offset: number): Promise<Post[]> => {
    const activeFilter = filterRef.current;
    let query = supabase
      .from('posts')
      .select(`*, author:users(id, username, avatar_url, level, ispremium)`)
      .not('media_url', 'is', null)
      .order('created_at', { ascending: false });

    if (activeFilter === 'images') query = query.eq('media_type', 'image');
    else if (activeFilter === 'videos') query = query.eq('media_type', 'video');

    query = query.range(offset, offset + limit - 1);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPosts = async () => {
      setPosts([]);
      setHasMore(true);
      isFetchingRef.current = false;
      setLoading(true);
      try {
        const fetchedPosts = await fetchGalleryPosts(20, 0);
        if (cancelled) return;
        const unique = Array.from(new Map(fetchedPosts.map(p => [p.id, p])).values());
        setPosts(unique);
        if (fetchedPosts.length < 20) setHasMore(false);

        const { data: { user } } = await supabase.auth.getUser();
        if (user && !cancelled) {
          const { data: userLikes } = await supabase
            .from('likes').select('post_id').eq('user_id', user.id);
          if (userLikes) setLikedPosts(new Set(userLikes.map((l: { post_id: string }) => l.post_id)));
        }
      } catch {
        if (!cancelled) toast.error('Failed to load gallery');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadPosts();
    return () => { cancelled = true; };
  }, [filter, fetchGalleryPosts]);

  const loadMorePosts = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current || isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const morePosts = await fetchGalleryPosts(20, postsRef.current.length);
      if (morePosts.length === 0) {
        setHasMore(false);
      } else {
        setPosts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const uniqueNew = morePosts.filter(p => !existingIds.has(p.id));
          if (uniqueNew.length === 0) { setHasMore(false); return prev; }
          return [...prev, ...uniqueNew];
        });
        if (morePosts.length < 20) setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more:', error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [fetchGalleryPosts]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMorePosts(); },
      { threshold: 0.5 }
    );
    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [loadMorePosts]);

  const handleLike = async (postId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error('Please login to like posts'); return; }
    const isLiked = likedPosts.has(postId);
    setLikedPosts(prev => {
      const s = new Set(prev);
      isLiked ? s.delete(postId) : s.add(postId);
      return s;
    });
    try {
      if (isLiked) await supabase.from('likes').delete().eq('post_id', postId).eq('user_id', user.id);
      else await supabase.from('likes').insert({ post_id: postId, user_id: user.id });
      const { data: up } = await supabase.from('posts').select('likes').eq('id', postId).single();
      if (up) {
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: up.likes } : p));
        if (selectedPost?.id === postId) setSelectedPost(prev => prev ? { ...prev, likes: up.likes } : null);
      }
    } catch {
      toast.error('Failed to like');
      setLikedPosts(prev => {
        const s = new Set(prev);
        isLiked ? s.add(postId) : s.delete(postId);
        return s;
      });
    }
  };

  const toggleSave = (postId: string) => {
    setSavedPosts(prev => {
      const s = new Set(prev);
      if (s.has(postId)) { s.delete(postId); toast.success('Removed from saved'); }
      else { s.add(postId); toast.success('Saved! 📌'); }
      return s;
    });
  };

  const handleShare = (postId: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/post/${postId}`);
    toast.success('Link copied! 🔗');
  };

  const navigatePost = useCallback((direction: 'prev' | 'next') => {
    if (!selectedPost) return;
    const idx = posts.findIndex(p => p.id === selectedPost.id);
    const newIdx = direction === 'prev'
      ? (idx - 1 + posts.length) % posts.length
      : (idx + 1) % posts.length;
    setSelectedPost(posts[newIdx]);
  }, [selectedPost, posts]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!selectedPost) return;
      if (e.key === 'ArrowLeft') navigatePost('prev');
      if (e.key === 'ArrowRight') navigatePost('next');
      if (e.key === 'Escape') setSelectedPost(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedPost, navigatePost]);

  // Lock scroll when lightbox open
  useEffect(() => {
    document.body.style.overflow = selectedPost ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedPost]);

  const formatTime = (iso: string) => {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
    if (diff < 1) return 'Just now';
    if (diff < 24) return `${diff}h ago`;
    const days = Math.floor(diff / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  };

  const formatCount = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toString();
  };

  // ── Lightbox portal ────────────────────────────────────────────
  const lightbox = selectedPost
    ? ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.95)' }}
          className="flex items-center justify-center p-4"
        >
          {/* Close */}
          <button
            onClick={() => setSelectedPost(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all z-10"
          >
            <FaTimes className="text-lg" />
          </button>

          {/* Prev / Next */}
          <button
            onClick={() => navigatePost('prev')}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all z-10"
          >
            <FaChevronLeft />
          </button>
          <button
            onClick={() => navigatePost('next')}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all z-10"
          >
            <FaChevronRight />
          </button>

          <div className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Media */}
              <div className="lg:w-2/3 flex items-center justify-center bg-black rounded-2xl overflow-hidden min-h-[300px]">
                {selectedPost.media_type === 'video' ? (
                  <video src={selectedPost.media_url} controls className="w-full h-auto max-h-[80vh] object-contain" />
                ) : (
                  <img
                    src={selectedPost.media_url}
                    alt={selectedPost.content}
                    className="w-full h-auto max-h-[80vh] object-contain"
                  />
                )}
              </div>

              {/* Info panel */}
              <div className={`lg:w-1/3 rounded-2xl p-6 flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={selectedPost.author?.avatar_url || 'https://i.pravatar.cc/150'}
                    alt={selectedPost.author?.username}
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedPost.author?.username || 'Anonymous'}
                      </span>
                      {selectedPost.author?.isPremium && <FaStar className="text-yellow-400 text-xs" />}
                    </div>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {formatTime(selectedPost.created_at)}
                    </p>
                  </div>
                </div>

                <p className={`text-sm mb-4 leading-relaxed flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {selectedPost.content}
                </p>

                <div className={`flex items-center gap-4 py-4 border-t border-b mb-4 ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                  <button
                    onClick={() => handleLike(selectedPost.id)}
                    className={`flex items-center gap-1.5 text-sm font-medium transition-all ${
                      likedPosts.has(selectedPost.id) ? 'text-purple-500' : darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <FaHeart className={likedPosts.has(selectedPost.id) ? 'fill-current' : ''} />
                    {formatCount(selectedPost.likes)}
                  </button>
                  <button className={`flex items-center gap-1.5 text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <FaComment />
                    {formatCount(selectedPost.comments_count)}
                  </button>
                  <button
                    onClick={() => toggleSave(selectedPost.id)}
                    className={`ml-auto transition-all ${savedPosts.has(selectedPost.id) ? 'text-purple-500' : darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                  >
                    <FaBookmark className={savedPosts.has(selectedPost.id) ? 'fill-current' : ''} />
                  </button>
                  <button
                    onClick={() => handleShare(selectedPost.id)}
                    className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}
                  >
                    <FaShare />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: 'Likes', val: selectedPost.likes },
                    { label: 'Comments', val: selectedPost.comments_count },
                    { label: 'Shares', val: selectedPost.shares },
                  ].map(({ label, val }) => (
                    <div key={label} className={`rounded-xl py-3 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCount(val)}</div>
                      <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Gallery</h1>
          <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {posts.length} {filter === 'all' ? 'media items' : filter}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['all', 'images', 'videos'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                filter === f
                  ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg shadow-purple-500/25'
                  : darkMode ? 'bg-gray-800/80 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900'
              }`}
            >
              {f === 'all' && <FaFilter className="text-xs" />}
              {f === 'images' && <FaImages className="text-xs" />}
              {f === 'videos' && <FaVideo className="text-xs" />}
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Bento Grid ── */}
      {posts.length === 0 && !loading ? (
        <div className="text-center py-20">
          <FaImages className={`text-6xl mx-auto mb-4 ${darkMode ? 'text-gray-800' : 'text-gray-300'}`} />
          <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            No {filter === 'all' ? 'media' : filter} yet
          </h3>
          <p className={`text-sm ${darkMode ? 'text-gray-600' : 'text-gray-500'}`}>
            Create a post with an image or video to see it here
          </p>
        </div>
      ) : (
        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: 'repeat(4, 1fr)',
            gridAutoRows: '180px',
          }}
        >
          {posts.map((post, index) => {
            const bentoClass = getBentoClass(index);
            const isLarge = bentoClass.includes('col-span-2') && bentoClass.includes('row-span-2');

            return (
              <div
                key={post.id}
                className={`${bentoClass} relative group cursor-pointer overflow-hidden rounded-2xl ${
                  darkMode ? 'bg-gray-900' : 'bg-gray-100'
                }`}
                onClick={() => setSelectedPost(post)}
              >
                {/* Media */}
                <img
                  src={post.media_url}
                  alt={post.content}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Video badge */}
                {post.media_type === 'video' && (
                  <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-full p-2">
                    <FaPlay className="text-white text-xs" />
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                  {/* Expand icon top right */}
                  <div className="absolute top-3 right-3">
                    <div className={`bg-white/15 backdrop-blur-sm rounded-full p-1.5 ${isLarge ? 'block' : 'hidden'}`}>
                      <FaExpand className="text-white text-xs" />
                    </div>
                  </div>

                  {/* Bottom info */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    {isLarge && (
                      <div className="flex items-center gap-2 mb-2">
                        <img
                          src={post.author?.avatar_url || 'https://i.pravatar.cc/150'}
                          alt={post.author?.username}
                          className="w-6 h-6 rounded-full object-cover border border-white/30"
                        />
                        <span className="text-white text-xs font-medium truncate">
                          {post.author?.username}
                        </span>
                        {post.author?.isPremium && <FaStar className="text-yellow-400 text-xs flex-shrink-0" />}
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-white">
                      <span className="flex items-center gap-1 text-xs font-medium">
                        <FaHeart className={likedPosts.has(post.id) ? 'text-purple-400' : ''} />
                        {formatCount(post.likes)}
                      </span>
                      <span className="flex items-center gap-1 text-xs font-medium">
                        <FaComment />
                        {formatCount(post.comments_count)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Skeleton placeholders while loading */}
          {loading && [...Array(4)].map((_, i) => (
            <div
              key={`skel-${i}`}
              className={`${getBentoClass(posts.length + i)} rounded-2xl animate-pulse ${
                darkMode ? 'bg-gray-800' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      )}

      <div ref={observerTarget} className="h-10 mt-4" />

      {!hasMore && posts.length > 0 && (
        <p className={`text-center text-sm mt-6 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          You've seen it all ✨
        </p>
      )}

      {lightbox}
    </div>
  );
}